import type { MouseEvent } from "react";
import { MapPin, Swords } from "lucide-react";

import { Avatar, Badge, getInitials, tintForId } from "@/ui";
import { formatEventDate } from "@/lib/format";
import { TypeBadge } from "@/features/events/EventCard";
import { useOpenUserProfile } from "@/features/users/UserProfileSheetProvider";
import type { Challenge, ChallengeStatus } from "@/types/api";

interface Props {
  challenge: Challenge;
  onClick?: () => void;
}

const STATUS_LABEL: Record<ChallengeStatus, string> = {
  pending: "Ожидает",
  accepted: "Принят",
  declined: "Отклонён",
  cancelled: "Отменён",
  completed: "Сыгран",
};

const STATUS_KIND: Record<ChallengeStatus, "brand" | "success" | "neutral"> = {
  pending: "brand",
  accepted: "success",
  declined: "neutral",
  cancelled: "neutral",
  completed: "neutral",
};

const avatarBtn: React.CSSProperties = {
  background: "transparent",
  border: 0,
  padding: 0,
  cursor: "pointer",
  borderRadius: "50%",
  flex: "none",
  display: "inline-flex",
};

function briefName(u: Challenge["challenger"]): string {
  return (
    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
    u.username ||
    `игрок #${u.id}`
  );
}

export function ChallengeCard({ challenge, onClick }: Props) {
  const openProfile = useOpenUserProfile();
  const when = challenge.scheduled_at
    ? formatEventDate(challenge.scheduled_at)
    : "сейчас";

  // Stop propagation so tapping a player avatar opens their profile
  // without also firing the card's own onClick.
  const openSide = (id: number) => (e: MouseEvent) => {
    e.stopPropagation();
    openProfile(id);
  };

  return (
    <article
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      style={{
        background: "var(--bg-section)",
        borderRadius: "var(--r-lg)",
        padding: 16,
        margin: "0 16px 12px",
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Badge kind={STATUS_KIND[challenge.status]}>
          {STATUS_LABEL[challenge.status]}
        </Badge>
        <TypeBadge kind="challenge" />
        <span style={{ marginLeft: "auto", fontSize: 13, color: "var(--fg2)" }}>
          {when}
        </span>
      </div>

      {challenge.spot_name && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 13,
            color: "var(--fg2)",
          }}
        >
          <MapPin size={14} color="var(--fg3)" />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {challenge.spot_name}
          </span>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button
          type="button"
          onClick={openSide(challenge.challenger.id)}
          aria-label="Профиль игрока"
          style={avatarBtn}
        >
          <Avatar
            initials={getInitials(
              challenge.challenger.first_name,
              null,
              challenge.challenger.username,
            )}
            size={36}
            tint={tintForId(challenge.challenger.id)}
            src={challenge.challenger.photo_url}
          />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="body"
            style={{
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {briefName(challenge.challenger)}
          </div>
          {challenge.challenger.rating != null && (
            <div className="caption" style={{ color: "var(--fg3)" }}>
              {challenge.challenger.rating}
            </div>
          )}
        </div>

        <Swords size={18} color="var(--brand-orange)" />

        <div style={{ flex: 1, minWidth: 0, textAlign: "right" }}>
          <div
            className="body"
            style={{
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {briefName(challenge.challenged)}
          </div>
          {challenge.challenged.rating != null && (
            <div className="caption" style={{ color: "var(--fg3)" }}>
              {challenge.challenged.rating}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={openSide(challenge.challenged.id)}
          aria-label="Профиль игрока"
          style={avatarBtn}
        >
          <Avatar
            initials={getInitials(
              challenge.challenged.first_name,
              null,
              challenge.challenged.username,
            )}
            size={36}
            tint={tintForId(challenge.challenged.id)}
            src={challenge.challenged.photo_url}
          />
        </button>
      </div>
    </article>
  );
}
